'use client';

import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import type { RefinementProps } from '@/components/eataly-plp-filters/lib/types';
import { SvgSearch, SvgClose } from '@/components/icons-eataly';
import { filterRefinementValues } from '@/components/eataly-plp-filters/lib/filter-refinement-values';
import RefineCheckboxList from '@/components/eataly-plp-filters/refine-checkbox-list';
import { cn } from '@/lib/utils';

export type EatalyRefineSearchableListProps = RefinementProps & {
    label?: string;
};

export default function EatalyRefineSearchableList({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
    isPending,
    label,
}: EatalyRefineSearchableListProps): ReactElement {
    const { t } = useTranslation(['categoryPlp']);
    const searchRefinementPlaceholder = t('categoryPlp:filtersDrawer.searchRefinementPlaceholder', { label });
    const [query, setQuery] = useState('');
    const refinementLabel = label ?? attributeId;
    const filteredValues = useMemo(() => filterRefinementValues(values, query), [values, query]);

    return (
        <div className="flex flex-col gap-4 pt-4">
            <div className="relative">
                <Input
                    id={`refine-search-${attributeId}`}
                    type="text"
                    value={query}
                    variant="search"
                    placeholder={searchRefinementPlaceholder}
                    className={cn(
                        'w-full',
                        'h-10 rounded-[var(--corner-24)] border border-[var(--sand-90)] bg-[var(--sand-95)] py-2 text-base leading-6 tracking-[var(--tracking-body-l)] md:text-base',
                        'text-[color:var(--neutral-10)] caret-[color:var(--brown-30)]',
                        'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--sand-90)]',
                        'pl-11 pr-12',
                        query.trim().length === 0
                            ? 'placeholder:text-[color:var(--neutral-10)]'
                            : 'placeholder:text-[color:var(--neutral-40)]'
                    )}
                    onChange={(e) => setQuery(e.target.value)}
                    disabled={isPending}
                    aria-label={t('categoryPlp:filtersDrawer.searchRefinementAriaLabel', {
                        label: refinementLabel,
                    })}
                />
                {query.trim().length === 0 ? (
                    <SvgSearch
                        className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2"
                        aria-hidden
                    />
                ) : (
                    <>
                        <button
                            type="button"
                            disabled={isPending}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setQuery('')}
                            className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-[color:var(--neutral-10)] cursor-pointer"
                            aria-label={t('clearSearch', 'Clear search')}>
                            <SvgClose className="size-5" aria-hidden />
                        </button>
                    </>
                )}
            </div>

            <RefineCheckboxList
                values={filteredValues}
                attributeId={attributeId}
                isFilterSelected={isFilterSelected}
                toggleFilter={toggleFilter}
                isPending={isPending}
                emptyMessage={query.trim() ? t('categoryPlp:filtersDrawer.searchRefinementNoResults') : undefined}
            />
        </div>
    );
}
